import { CustomTicketV2, PromoCode, PromoCodeType } from "@markit/common.types";
import { MAX_TICKET_PRICE } from "../makeEvent";
import { isPaidTicketType } from "@markit/common.utils";

// Checks if the named price by the user is invalid, meaning less than the min price, or greater than the max price
export const hasInvalidFlexiblePriceInput = (
  selectedCustomTickets: CustomTicketV2[],
  namedPrices: Map<string, number>
) => {
  return selectedCustomTickets.some((customTicket) => {
    const namedPrice = namedPrices.get(customTicket.id) ?? 0;
    return (
      customTicket.flexiblePricing &&
      (namedPrice < customTicket.minPrice || namedPrice > MAX_TICKET_PRICE)
    );
  });
};

// Checks if the total amount is invalid because it's a paid transaction below the minimum ($1)
export const isTotalBelowStripeMinimum = (totalAmountCents: number): boolean =>
  totalAmountCents > 0 && totalAmountCents < 100;

// Get the effective price of a ticket, taking into account the named price if it exists
export const getEffectivePriceCents = (
  ticket: CustomTicketV2,
  namedPrices: Map<string, number>
) =>
  ticket.flexiblePricing
    ? (namedPrices.get(ticket.id) ?? ticket.price)
    : ticket.price;

// Count the number of paid tickets, taking into account the named price if it exists
export const countPaidTickets = (
  selectedCustomTickets: CustomTicketV2[],
  ticketQuantities: Map<string, number>,
  namedPrices: Map<string, number>
) =>
  selectedCustomTickets.reduce((sum, ticket) => {
    if (!isPaidTicketType(ticket)) return sum;
    if (
      ticket.flexiblePricing &&
      getEffectivePriceCents(ticket, namedPrices) === 0
    )
      return sum;
    return sum + (ticketQuantities.get(ticket.id) ?? 0);
  }, 0);

// If priceCents is passed and the discount fully covers it, returns "Free" instead of the off-amount
export const getPromoCodeDiscountLabel = (
  promoCode: PromoCode,
  priceCents?: number
) => {
  const discount = promoCode.constants[0];
  if (promoCode.type === PromoCodeType.PERCENT_OFF) {
    return discount >= 100 ? "Free" : `${discount}% Off`;
  }
  return priceCents !== undefined && discount >= priceCents
    ? "Free"
    : `$${discount / 100} Off`;
};
